import type { Card, ClassicCard } from "@braze/web-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";

import { APP_V2_APP_EVENTS, logEvent } from "../analytics";

const NOTIFICATION_CENTER_FILTER = {
  key: "type",
  value: "NOTIFICATION_CENTER",
} as const;

function isClassicCard(card: Card): card is ClassicCard {
  return "title" in card && "description" in card;
}

function filterNotificationCards(cards: Card[]): Card[] {
  return cards.filter(
    (card) => card.extras?.[NOTIFICATION_CENTER_FILTER.key] === NOTIFICATION_CENTER_FILTER.value
  );
}

export interface Notification {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly imageUrl?: string;
  readonly url?: string;
  readonly linkText?: string;
  readonly isViewed: boolean;
  readonly isPinned: boolean;
  readonly createdAt: Date | null;
  readonly extras: Record<string, string>;
}

/**
 * Transform a Braze content card into our normalized notification format
 */
function transformCard(card: Card): Notification {
  // For cards with title/description (ClassicCard, CaptionedImage)
  if (isClassicCard(card)) {
    return {
      id: card.id ?? "",
      title: card.title ?? "",
      description: card.description ?? "",
      imageUrl: card.imageUrl,
      url: card.url,
      linkText: card.linkText,
      isViewed: card.viewed,
      isPinned: card.pinned,
      createdAt: card.updated,
      extras: card.extras,
    };
  }

  // For other card types (ImageOnly, ControlCard)
  return {
    id: card.id ?? "",
    title: "",
    description: "",
    imageUrl: undefined,
    url: undefined,
    linkText: undefined,
    isViewed: card.viewed,
    isPinned: card.pinned,
    createdAt: card.updated,
    extras: card.extras,
  };
}

export interface UseBrazeNotificationsOptions {
  /**
   * Auto-refresh interval in milliseconds. Set to 0 to disable auto-refresh.
   * @default 60000 (1 minute)
   */
  refreshInterval?: number;
}

export function useBrazeNotifications(
  brazeInitialized: boolean,
  options: UseBrazeNotificationsOptions = {}
): {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  hasError: boolean;
  refresh: () => void;
  markAsClicked: (notificationId: string) => void;
  dismiss: (notificationId: string) => void;
  markAsDisplayed: (notificationIds: string[]) => void;
} {
  const { refreshInterval = 60_000 } = options;

  const [cards, setCards] = useState<Card[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Refresh content cards from Braze
  const refresh = useCallback(() => {
    const { braze } = globalThis;
    if (!braze || !brazeInitialized) {
      return;
    }

    braze.requestContentCardsRefresh(
      () => {
        const contentCards = braze.getCachedContentCards();
        if (contentCards) {
          const filteredCards = filterNotificationCards(contentCards.cards);
          setCards(filteredCards);
        }
        setIsLoading(false);
      },
      () => {
        setHasError(true);
        logEvent(APP_V2_APP_EVENTS.REFRESH_NOTIFICATIONS_FAILURE);
      }
    );
  }, [brazeInitialized]);

  // Initial fetch and subscription
  useEffect(() => {
    const { braze } = globalThis;
    if (!braze || !brazeInitialized) {
      return;
    }

    // Subscribe to content card updates
    const subscriptionId = braze.subscribeToContentCardsUpdates((contentCards) => {
      const filteredCards = filterNotificationCards(contentCards.cards);
      setCards(filteredCards);
      setIsLoading(false);
    });

    // Initial refresh
    refresh();

    return () => {
      if (subscriptionId) {
        braze.removeSubscription(subscriptionId);
      }
    };
  }, [brazeInitialized, refresh]);

  // Auto-refresh interval
  useEffect(() => {
    if (refreshInterval <= 0 || !brazeInitialized) {
      return;
    }

    const intervalId = setInterval(refresh, refreshInterval);

    return () => {
      clearInterval(intervalId);
    };
  }, [refresh, refreshInterval, brazeInitialized]);

  // Transform cards to notifications
  const notifications = useMemo(() => cards.map(transformCard), [cards]);

  // Calculate unread count
  const unreadCount = useMemo(() => cards.filter((card) => !card.viewed).length, [cards]);

  // Mark a notification as clicked
  const markAsClicked = useCallback(
    (notificationId: string) => {
      const { braze } = globalThis;
      if (!braze) {
        return;
      }

      const card = cards.find((c) => c.id === notificationId);
      if (card) {
        braze.logContentCardClick(card);
      }
    },
    [cards]
  );

  // Dismiss a notification
  const dismiss = useCallback(
    (notificationId: string) => {
      const { braze } = globalThis;
      if (!braze) {
        return;
      }

      const card = cards.find((c) => c.id === notificationId);
      if (card) {
        card.dismissCard();
        braze.logCardDismissal(card);
        // Update local state to remove dismissed card
        setCards((previous) => previous.filter((c) => c.id !== notificationId));
      }
    },
    [cards]
  );

  // Log impression for displayed notifications
  const markAsDisplayed = useCallback(
    (notificationIds: string[]) => {
      const { braze } = globalThis;
      if (!braze) {
        return;
      }

      const cardsToLog = cards.filter((card) => card.id && notificationIds.includes(card.id));
      if (cardsToLog.length > 0) {
        // Use logCardImpressions - the CDN SDK has this method but npm types don't include it
        // This is the same method NotificationCenterButton uses
        (
          braze as unknown as {
            logCardImpressions: (cards: Card[], forContentCards: boolean) => void;
          }
        ).logCardImpressions(cardsToLog, true);
      }
    },
    [cards]
  );

  return {
    notifications,
    unreadCount,
    isLoading,
    hasError,
    refresh,
    markAsClicked,
    dismiss,
    markAsDisplayed,
  };
}
